/**
 * Stage D1 — Order Source master + manual payment vocabulary.
 * Presentation/validation constants only; the database CHECK constraints
 * (`orders_order_channel_check`, `orders_manual_payment_method_check`,
 * `customer_profiles_acquisition_source_check`) remain the enforcement point.
 */

export const ORDER_SOURCES = ["legacy", "mp2", "cart"] as const;
export type OrderSource = (typeof ORDER_SOURCES)[number];

export const ORDER_SOURCE_LABEL: Record<OrderSource, string> = {
  legacy: "Legacy",
  mp2: "MP2",
  cart: "Cart",
};

export function orderSourceLabel(value: string | null | undefined): string {
  if (!value) return "Website";
  return ORDER_SOURCE_LABEL[value as OrderSource] ?? value;
}

/** Manual orders bypass Razorpay; the collected/expected method is recorded here. */
export const MANUAL_PAYMENT_METHODS = [
  "pending",
  "paid",
  "cash",
  "upi",
  "card",
  "cod",
  "to_be_collected",
] as const;
export type ManualPaymentMethod = (typeof MANUAL_PAYMENT_METHODS)[number];

export const MANUAL_PAYMENT_LABEL: Record<ManualPaymentMethod, string> = {
  pending: "Pending",
  paid: "Paid",
  cash: "Cash",
  upi: "UPI",
  card: "Card",
  cod: "COD",
  to_be_collected: "To be collected",
};

export function manualPaymentLabel(value: string | null | undefined): string {
  if (!value) return "—";
  return MANUAL_PAYMENT_LABEL[value as ManualPaymentMethod] ?? value;
}

/** Primary acquisition source on the customer profile — never set automatically. */
export const ACQUISITION_SOURCES = [
  "website",
  "phone",
  "walkin",
  "whatsapp",
  "corporate",
  "referral",
  "other",
] as const;
export type AcquisitionSource = (typeof ACQUISITION_SOURCES)[number];

export const ACQUISITION_SOURCE_LABEL: Record<AcquisitionSource, string> = {
  website: "Website",
  phone: "Phone",
  walkin: "Walk-in",
  whatsapp: "WhatsApp",
  corporate: "Corporate",
  referral: "Referral",
  other: "Other",
};

/** Filterable order sources shown on the Orders screen. */
export const ORDER_SOURCE_FILTER_OPTIONS: { value: OrderSource; label: string }[] = [
  "legacy",
  "mp2",
  "cart",
].map((v) => ({ value: v as OrderSource, label: ORDER_SOURCE_LABEL[v as OrderSource] }));

/**
 * Entry mode records HOW the order was captured, independent of its source:
 * `customer` = website self-service checkout, `admin` = Manual Orders workflow.
 */
export const ENTRY_MODES = ["customer", "admin"] as const;
export type EntryMode = (typeof ENTRY_MODES)[number];

export const ENTRY_MODE_LABEL: Record<EntryMode, string> = {
  customer: "Customer",
  admin: "Admin",
};

export function entryModeLabel(value: string | null | undefined): string {
  if (!value) return "Customer";
  return ENTRY_MODE_LABEL[value as EntryMode] ?? value;
}
